import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { paymentOrders, type PaymentOrder } from '@/api/paymentOrders';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const PERMISSION = 'documents.payment_orders.create';

const STATE_LABEL: Record<string, string> = {
  DRAFT: 'Чернетка',
  SENT_TO_BANK: 'Відправлено',
  EXECUTED: 'Виконано',
  CANCELLED: 'Скасовано',
};

const DIRECTION_LABEL: Record<string, string> = {
  IN: '↓ Надходження',
  OUT: '↑ Списання',
};

export default function PaymentOrdersPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const [orgId, setOrgId] = useState<string>('');
  useEffect(() => {
    const first = orgList.data?.items[0];
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['payment-orders', orgId],
    queryFn: () => paymentOrders.list({ organization_id: orgId || undefined, limit: 500 }),
    enabled: !!orgId,
  });

  const cols = useMemo<ColumnDef<PaymentOrder>[]>(
    () => [
      { key: 'document_number', header: 'Номер', width: 130, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      {
        key: 'document_date',
        header: 'Дата',
        width: 110,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => new Date(r.document_date).toLocaleDateString('uk-UA'),
      },
      {
        key: 'direction',
        header: 'Напрямок',
        width: 140,
        sortValue: (r) => r.direction ?? '',
        cell: (r) => DIRECTION_LABEL[r.direction] ?? r.direction,
      },
      {
        key: 'state',
        header: 'Стан',
        width: 130,
        sortValue: (r) => r.state ?? '',
        cell: (r) => STATE_LABEL[r.state] ?? r.state,
      },
      { key: 'amount_uah', header: 'UAH', width: 130, align: 'right', sortValue: (r) => Number(r.amount_uah), cell: (r) => r.amount_uah },
      { key: 'payment_purpose', header: 'Призначення', sortValue: (r) => r.payment_purpose ?? '', cell: (r) => r.payment_purpose },
      {
        key: 'posted',
        header: 'Проведено',
        width: 100,
        align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓' : '—'),
      },
    ],
    [],
  );

  const canCreate = !!orgId && allowedOrgIds.has(orgId);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Платіжні ордери</h2>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ width: 140, flex: '0 0 auto' }}>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.code} — {o.name}
            </option>
          ))}
        </select>
        <button
          className="primary"
          style={{ marginLeft: 'auto' }}
          disabled={!canCreate}
          onClick={() => navigate(`/banking/payment-orders/new?org=${orgId}`)}
        >
          + Платіжний ордер
        </button>
      </div>
      <DataTable<PaymentOrder>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/banking/payment-orders/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
